import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Upload, Download, Trash2, Loader2, Plus, FileUp, Calendar, User, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ModuliUtiliPage = () => {
  const { user, getAuthHeader, impersonatedUser, getImpersonateParams } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(null);
  
  // Dialog per modifica visibilità
  const [editVisibilityDialog, setEditVisibilityDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingReparti, setEditingReparti] = useState([]);
  
  // Form state per upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [titolo, setTitolo] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  // Reparti per visibilità
  const [repartiDisponibili, setRepartiDisponibili] = useState([]);
  const [selectedReparti, setSelectedReparti] = useState([]); // Vuoto = tutti

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isImpersonating = !!impersonatedUser;

  const fetchModules = useCallback(async () => {
    try {
      // Passa view_as se in impersonificazione
      const params = getImpersonateParams ? getImpersonateParams() : {};
      const queryString = params.view_as ? `?view_as=${params.view_as}` : '';
      const response = await axios.get(`${API}/modules${queryString}`, { headers: getAuthHeader() });
      setModules(response.data);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Errore nel caricamento dei moduli');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, getImpersonateParams]);

  // Carica lista reparti disponibili (solo per Admin)
  const fetchRepartiDisponibili = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/modules/reparti-disponibili`, { headers: getAuthHeader() });
      setRepartiDisponibili(response.data);
    } catch (error) {
      console.error('Error fetching reparti:', error);
    }
  }, [getAuthHeader, isAdmin]);

  useEffect(() => {
    fetchModules();
    fetchRepartiDisponibili();
  }, [fetchModules, fetchRepartiDisponibili]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Formato non valido. Sono ammessi solo file PDF.');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Formato non valido. Sono ammessi solo file PDF.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Seleziona un file PDF');
      return;
    }
    if (!titolo.trim()) {
      toast.error('Inserisci un titolo per il modulo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('titolo', titolo.trim());
      // Invia i reparti selezionati (vuoto = tutti)
      formData.append('reparti_visibili', selectedReparti.join(','));

      await axios.post(`${API}/modules/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Modulo "${titolo}" caricato con successo`);
      setUploadDialog(false);
      setSelectedFile(null);
      setTitolo('');
      setSelectedReparti([]);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (module) => {
    setDownloadLoading(module.id);
    try {
      const response = await axios.get(`${API}/modules/${module.id}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });

      // Crea un link per il download - usa il titolo come nome file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Usa il titolo del modulo come nome file, non il filename originale
      const downloadName = module.titolo.endsWith('.pdf') ? module.titolo : `${module.titolo}.pdf`;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Download di "${module.titolo}" completato`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Errore nel download del file');
    } finally {
      setDownloadLoading(null);
    }
  };

  // Apri dialog per modifica visibilità
  const openEditVisibility = (module) => {
    setEditingModule(module);
    setEditingReparti(module.reparti_visibili || []);
    setEditVisibilityDialog(true);
  };

  // Salva modifica visibilità
  const handleSaveVisibility = async () => {
    if (!editingModule) return;
    
    try {
      await axios.patch(
        `${API}/modules/${editingModule.id}/visibility`,
        { reparti_visibili: editingReparti },
        { headers: getAuthHeader() }
      );
      
      toast.success('Visibilità aggiornata');
      setEditVisibilityDialog(false);
      setEditingModule(null);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (module) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${module.titolo}"?`)) {
      return;
    }

    setDeleteLoading(module.id);
    try {
      await axios.delete(`${API}/modules/${module.id}`, { headers: getAuthHeader() });
      toast.success(`Modulo "${module.titolo}" eliminato`);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="moduli-utili-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Moduli Utili
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Gestisci i documenti e moduli PDF per il team' : 'Scarica i documenti e moduli utili'}
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => setUploadDialog(true)}
            className="bg-primary hover:bg-primary/90"
            data-testid="upload-module-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Carica Nuovo Modulo
          </Button>
        )}
      </div>

      {/* Modules List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            <FileText className="w-5 h-5 text-primary" />
            Elenco Moduli
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : modules.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Nessun modulo disponibile</p>
              {isAdmin && (
                <p className="text-sm mt-2">Clicca su "Carica Nuovo Modulo" per aggiungerne uno</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Titolo</TableHead>
                    <TableHead>Dimensione</TableHead>
                    {isSuperAdmin && <TableHead>Visibilità</TableHead>}
                    <TableHead>Caricato da</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modules.map(module => (
                    <TableRow key={module.id} data-testid={`module-row-${module.id}`}>
                      <TableCell>
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-red-600" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-semibold text-slate-800">{module.titolo}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-500">{formatFileSize(module.file_size)}</p>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {module.reparti_nomi?.map((nome, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className={nome === 'Tutti' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}
                              >
                                {nome}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {module.uploaded_by_name}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(module.created_at)}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(module)}
                            disabled={downloadLoading === module.id}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`download-module-${module.id}`}
                          >
                            {downloadLoading === module.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                Scarica
                              </>
                            )}
                          </Button>
                          {/* Pulsante modifica visibilità - Solo Super Admin e non in impersonificazione */}
                          {isSuperAdmin && !isImpersonating && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditVisibility(module)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Modifica visibilità"
                              data-testid={`visibility-module-${module.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && !isImpersonating && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(module)}
                              disabled={deleteLoading === module.id}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-module-${module.id}`}
                            >
                              {deleteLoading === module.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="sm:max-w-md" data-testid="upload-module-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Carica Nuovo Modulo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Titolo */}
            <div className="space-y-2">
              <Label htmlFor="titolo">Titolo del Modulo *</Label>
              <Input
                id="titolo"
                value={titolo}
                onChange={(e) => setTitolo(e.target.value)}
                placeholder="Es: Informativa Privacy, Modulo Swap Ayvens..."
                data-testid="module-titolo-input"
              />
            </div>

            {/* File Upload con Drag & Drop */}
            <div className="space-y-2">
              <Label htmlFor="file">File PDF *</Label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                  isDragging 
                    ? 'border-primary bg-primary/5 scale-[1.02]' 
                    : selectedFile 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-slate-200 hover:border-primary/50'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="module-file-input"
                />
                <label htmlFor="file" className="cursor-pointer block">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-red-500" />
                      <div className="text-left">
                        <p className="font-medium text-slate-800">{selectedFile.name}</p>
                        <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>
                  ) : isDragging ? (
                    <div>
                      <FileUp className="w-12 h-12 mx-auto text-primary mb-2 animate-bounce" />
                      <p className="text-primary font-medium">Rilascia il file qui!</p>
                    </div>
                  ) : (
                    <div>
                      <FileUp className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                      <p className="text-slate-600">Trascina qui il file PDF</p>
                      <p className="text-slate-500 text-sm">oppure clicca per selezionare</p>
                      <p className="text-xs text-slate-400 mt-2">Solo formato .pdf</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Visibilità per Reparto (solo Super Admin) */}
            {isSuperAdmin && repartiDisponibili.length > 0 && (
              <div className="space-y-2">
                <Label>Visibile a:</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50">
                  {/* Opzione "Tutti" */}
                  <label className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReparti.length === 0}
                      onChange={() => setSelectedReparti([])}
                      className="w-4 h-4 text-primary rounded"
                    />
                    <span className="font-medium text-slate-700">Tutti i reparti</span>
                  </label>
                  
                  <hr className="my-2" />
                  
                  {/* Lista reparti */}
                  {repartiDisponibili.map(reparto => (
                    <label 
                      key={reparto.id} 
                      className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedReparti.includes(reparto.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReparti([...selectedReparti, reparto.id]);
                          } else {
                            setSelectedReparti(selectedReparti.filter(id => id !== reparto.id));
                          }
                        }}
                        className="w-4 h-4 text-primary rounded"
                      />
                      <span className={`text-slate-700 ${reparto.is_sede_centrale ? 'font-medium' : ''}`}>
                        {reparto.nome}
                        {reparto.is_sede_centrale && <span className="text-xs text-slate-400 ml-1">(Sede)</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  {selectedReparti.length === 0 
                    ? 'Il modulo sarà visibile a tutti i reparti'
                    : `Visibile a ${selectedReparti.length} reparto/i selezionato/i`
                  }
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setUploadDialog(false);
                  setSelectedFile(null);
                  setTitolo('');
                }}
                disabled={uploading}
              >
                Annulla
              </Button>
              <Button
                className="flex-1"
                onClick={handleUpload}
                disabled={uploading || !selectedFile || !titolo.trim()}
                data-testid="confirm-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Carica Modulo
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Visibilità */}
      <Dialog open={editVisibilityDialog} onOpenChange={setEditVisibilityDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-600" />
              Modifica Visibilità
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingModule && (
              <>
                <p className="text-sm text-slate-600">
                  Modulo: <span className="font-semibold">{editingModule.titolo}</span>
                </p>
                
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50">
                  {/* Opzione "Tutti" */}
                  <label className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingReparti.length === 0}
                      onChange={() => setEditingReparti([])}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <span className="font-medium text-slate-700">Tutti i reparti</span>
                  </label>
                  
                  <hr className="my-2" />
                  
                  {/* Lista reparti */}
                  {repartiDisponibili.map(reparto => (
                    <label 
                      key={reparto.id} 
                      className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editingReparti.includes(reparto.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingReparti([...editingReparti, reparto.id]);
                          } else {
                            setEditingReparti(editingReparti.filter(id => id !== reparto.id));
                          }
                        }}
                        className="w-4 h-4 text-amber-600 rounded"
                      />
                      <span className={`text-slate-700 ${reparto.is_sede_centrale ? 'font-medium' : ''}`}>
                        {reparto.nome}
                        {reparto.is_sede_centrale && <span className="text-xs text-slate-400 ml-1">(Sede)</span>}
                      </span>
                    </label>
                  ))}
                </div>
                
                <p className="text-xs text-slate-500">
                  {editingReparti.length === 0 
                    ? 'Il modulo sarà visibile a tutti i reparti'
                    : `Visibile a ${editingReparti.length} reparto/i selezionato/i`
                  }
                </p>
              </>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setEditVisibilityDialog(false);
                  setEditingModule(null);
                }}
              >
                Annulla
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                onClick={handleSaveVisibility}
              >
                <Eye className="w-4 h-4 mr-2" />
                Salva Visibilità
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModuliUtiliPage;
